import { Model, useRepo } from 'pinia-orm';
import { normalizeJson } from "../../shared/helpers";
import { useCommonStore } from "../../shared/state/common";
import * as users from "../services/UserService";
import Company from './Company';
import CompanyUser from './CompanyUser';
import Subscription from './Subscription';
import TimelineEvent from './TimelineEvent';
import Notification from './Notification';
import CalendarProfile from './CalendarProfile';
import Calendar from './Calendar';
import ShareJobCard from './ShareJobCard';
import ShareJobCardFavorite from './ShareJobCardFavorite';

export default class User extends Model {
    static fetched = false;
    static fetching = false;

    static entity = 'users';

    static fields() {
        return {
            id: this.uid(),
            default_company_id: this.attr(null),
            language: this.attr('en'),
            communication_language: this.attr(null),
            name: this.attr(''),
            first_name: this.attr(''),
            last_name: this.attr(''),
            email: this.attr(''),
            phone: this.attr(''),
            image_url: this.attr(null),
            pivot: this.attr(null),
            permissions: this.attr([]),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            seen_last_announcement: this.boolean(true),
            admin: this.boolean(false),
            two_factor_enabled: this.boolean(false),
            two_factor_state: this.attr(null),
            confetti: this.boolean(false),
            unsafe_password: this.boolean(false),
            primary_calendar_id: this.attr(null),
            is_sso_user: this.boolean(false),
            availability_duration: this.attr(null),
            availability_rules: this.attr([]),
            cronofy_id: this.attr(null),
            cronofy_auto_sync: this.boolean(false),
            time_zone: this.attr(null),

            //relationships
            companies: this.belongsToMany(Company, CompanyUser, 'user_id', 'company_id'),
            subscriptions: this.hasMany(Subscription, 'user_id'),
            timeline_events: this.hasMany(TimelineEvent, 'user_id'),
            notifications: this.morphMany(Notification, 'notifiable_id', 'notifiable_type'),
            companyUsers: this.hasMany(CompanyUser, 'user_id'),
            calendar_profiles: this.hasMany(CalendarProfile, 'user_id'),
            calendars: this.hasManyThrough(Calendar, CalendarProfile, 'user_id', 'calendar_profile_id'),
            calendar: this.belongsTo(Calendar, 'primary_calendar_id'),
            favoriteCards: this.belongsToMany(ShareJobCard, ShareJobCardFavorite, 'user_id', 'share_job_card_id'),
        }
    }

    static fetchUsers(extraIncludes = null) {
        this.fetching = true;

        return users.index(extraIncludes)
            .then((result) => {
                return useRepo(User).save(normalizeJson(result.data));
            }).then(() => {
                this.fetched = true;
                this.fetching = false;
            });
    }

    static allUsers() {
        return useRepo(User)
            .with('companyUsers')
            .get()
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    static allUsersExceptMe() {
        const commonStore = useCommonStore();
        return this.allUsers().filter(user => {
            return user.id !== commonStore.currentUserId;
        });
    }

    get initials() {
        return this.name.length ? this.name.split(' ').slice(0, 2)
            .map(part => part.charAt(0).toUpperCase())
            .join('') : '';
    }

    get twoFactorRequired() {
        return this.two_factor_state === 'required';
    }
}
